import { Document, DocumentVersion } from "@models";
import { DocumentStatus } from "@types";
import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getDocumentNavigationUrl, getDocVersionStatus } from "src/utils";

import {
  useCreateDocumentVersionMutation,
  usePatchDocumentVersionMutation,
} from "./mutations";
import { useGetUser } from "./queries";

type UseDocumentParams = {
  document?: Document | null;
  selectedDocVersion?: DocumentVersion;
  orgId?: string;
  deviceId?: string;
};

type UseDocumentResult = {
  // document model + lifecycle state
  documentStatus: DocumentStatus;
  mostRecentVersion: DocumentVersion | undefined;
  isLocked: boolean;

  // capability flags for the lifecycle actions below
  canSaveOfficialVersion: boolean;
  canCreateNewRevision: boolean;

  // lifecycle actions. saveOfficialVersion resolves with the Tiptap Cloud
  // version that was pinned for approval (or null), so the caller can preview it.
  saveOfficialVersion: () => Promise<number | null>;
  resumeEditing: () => Promise<void>;
  createNewRevision: () => Promise<void>;

  // action pending flags
  isSavingOfficialVersion: boolean;
  isResumingEditing: boolean;
  isCreatingNewRevision: boolean;
};

/**
 * Centralizes the custom-document lifecycle (draft -> unapproved -> live) and
 * its CRUD actions for a single document. The parent owns the document query
 * and passes the resolved `document`/`selectedDocVersion` in; this hook owns the
 * document model state and the mutations that advance it, while editor-specific
 * presentation (the Tiptap preview, read-only lock, approval drawer) stays in
 * the consuming component.
 *
 * See frontend/docs/tiptap-doc-lifecycle.md for the lifecycle rules. A document
 * is locked once its most recent version (`versions[0]`) is saved for approval.
 */
export const useDocument = ({
  document,
  selectedDocVersion,
  orgId,
  deviceId = "",
}: UseDocumentParams): UseDocumentResult => {
  const navigate = useNavigate();
  const { data: user } = useGetUser();
  const isSavingOfficialVersionRef = useRef(false);
  const {
    isPending: isPatchDocumentVersionPending,
    mutateAsync: patchDocumentVersion,
  } = usePatchDocumentVersionMutation();
  const {
    isPending: isCreateDocumentVersionPending,
    mutateAsync: createDocumentVersion,
  } = useCreateDocumentVersionMutation(
    orgId ?? "",
    deviceId
  );

  const mostRecentVersion = document?.versions?.[0];

  const documentStatus =
    document && selectedDocVersion && user && orgId
      ? getDocVersionStatus({
          selectedDocVersion,
          document,
          user,
          orgId,
        })
      : DocumentStatus.DRAFT;
  // Once the most recent version is saved for approval the document is locked:
  // it can no longer be edited. Editing only resumes when a new draft version
  // (readyForApproval: false) becomes the most recent version.
  const isLocked = mostRecentVersion?.readyForApproval === true;
  // The "+" button to start the next revision only makes sense once the most
  // recent version is a finalized revision (approved). A draft already being
  // the most recent version means editing is in progress, not locked.
  const canCreateNewRevision = mostRecentVersion?.revision != null;
  const canSaveOfficialVersion = Boolean(
    document?.id &&
      document?.tiptapPath &&
      selectedDocVersion?.id &&
      documentStatus === DocumentStatus.DRAFT
  );

  const saveOfficialVersion = useCallback(async () => {
    if (isSavingOfficialVersionRef.current) {
      return null;
    }

    if (!document?.id || !selectedDocVersion?.id || !orgId) {
      throw new Error("Unable to save a version without a selected draft.");
    }

    try {
      isSavingOfficialVersionRef.current = true;
      const updatedVersion = await patchDocumentVersion({
        orgId,
        docId: document.id,
        deviceId,
        docVersionId: selectedDocVersion.id,
        data: { readyForApproval: true },
      });
      return updatedVersion.tiptapVersion ?? null;
    } finally {
      isSavingOfficialVersionRef.current = false;
    }
  }, [
    document?.id,
    deviceId,
    orgId,
    patchDocumentVersion,
    selectedDocVersion?.id,
  ]);

  const resumeEditing = useCallback(async () => {
    if (!document?.id || !orgId) {
      throw new Error("Unable to resume editing without a document.");
    }

    const versionId = document.versions?.[0]?.id;
    if (!versionId) {
      throw new Error("Unable to resume editing without a version.");
    }

    await patchDocumentVersion({
      orgId,
      docId: document.id,
      deviceId,
      docVersionId: versionId,
      data: { readyForApproval: false },
    });
  }, [
    document?.id,
    document?.versions,
    deviceId,
    orgId,
    patchDocumentVersion,
  ]);

  const createNewRevision = useCallback(async () => {
    if (!document?.id || !orgId) {
      throw new Error("Unable to create a revision without a document.");
    }

    const newVersion = await createDocumentVersion({
      docId: document.id,
    });

    if (newVersion?.id) {
      navigate(
        getDocumentNavigationUrl(orgId, deviceId, document.id, newVersion.id)
      );
    }
  }, [createDocumentVersion, deviceId, document?.id, navigate, orgId]);

  return {
    documentStatus,
    mostRecentVersion,
    isLocked,

    canSaveOfficialVersion,
    canCreateNewRevision,

    saveOfficialVersion,
    resumeEditing,
    createNewRevision,

    isSavingOfficialVersion: isPatchDocumentVersionPending,
    isResumingEditing: isPatchDocumentVersionPending,
    isCreatingNewRevision: isCreateDocumentVersionPending,
  };
};
